import React from 'react';
import { motion } from 'framer-motion';
import type { EraUnlock } from '../types';
import { ASSETS } from '../assetManifest.js';
import { CONSTANTS } from '../data.js';

type EraUnlockPopupProps = {
  eraUnlock: EraUnlock;
  onClose: () => void;
};

export const EraUnlockPopup: React.FC<EraUnlockPopupProps> = ({ eraUnlock, onClose }) => {
  return (
    <motion.div
      className="absolute inset-0 flex items-center justify-center z-50"
      style={{ background: 'rgba(26, 22, 17, 0.85)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="relative flex flex-col items-stretch w-[480px] max-w-[92vw] rounded-xl overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.surface} 0%, ${CONSTANTS.ui.theme.colors.background} 100%)`,
          border: `2px solid ${CONSTANTS.ui.theme.colors.primary}`,
          boxShadow: `${CONSTANTS.ui.theme.effects.ornateBoxShadow}, 0 0 30px rgba(218, 165, 32, 0.4)`
        }}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        {/* Decorative corners */}
        <div className="absolute top-3 left-3 w-8 h-8 border-l-2 border-t-2 rounded-tl-lg z-10" style={{ borderColor: CONSTANTS.ui.theme.colors.primary }} />
        <div className="absolute top-3 right-3 w-8 h-8 border-r-2 border-t-2 rounded-tr-lg z-10" style={{ borderColor: CONSTANTS.ui.theme.colors.primary }} />

        <div
          className="h-32 relative overflow-hidden"
          style={{
            backgroundImage: ASSETS.backgrounds?.[eraUnlock.key]?.path
              ? `linear-gradient(135deg, rgba(218, 165, 32, 0.3) 0%, rgba(139, 69, 19, 0.3) 100%), url(${ASSETS.backgrounds[eraUnlock.key].path})`
              : `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.primary}40 0%, ${CONSTANTS.ui.theme.colors.accent}40 100%)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0" style={{ background: 'rgba(26, 22, 17, 0.4)' }} />
          <div className="absolute bottom-4 left-6 right-6">
            <motion.div 
              className="font-crimson text-sm italic mb-1"
              style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              New Era Unlocked
            </motion.div>
            <motion.div 
              className="font-cinzel text-3xl font-bold"
              style={{ 
                color: CONSTANTS.ui.theme.colors.text,
                textShadow: CONSTANTS.ui.theme.effects.textGlow
              }}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              {eraUnlock.name}
            </motion.div>
          </div>
        </div>
        
        <div className="p-6 flex flex-col">
          <motion.div 
            className="font-crimson text-base leading-relaxed mb-6"
            style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            {eraUnlock.description}
          </motion.div>
          
          <motion.button
            onClick={onClose}
            className="font-crimson px-8 py-3 rounded-lg text-lg font-semibold transition-all duration-200 hover:scale-105 active:scale-95 self-end"
            style={{
              background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.success} 0%, #32cd32 100%)`,
              color: CONSTANTS.ui.theme.colors.background,
              border: `1px solid ${CONSTANTS.ui.theme.colors.success}`,
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
            }}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Continue Journey
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};
